import { isOfflineCapablePlayer } from "@/lib/platform";
import { isPlayerSurface } from "@/lib/player-surface";

/**
 * Register the player service worker scoped to this artifact's base path.
 * Registration is best-effort: failures (unsupported browser, insecure
 * context) must never block the app from running online.
 *
 * Gated to BOTH conditions, because this runs as a module-level side effect the
 * instant `player-runtime` is imported — and the desktop shell now loads its
 * CONTROL hub from the remote origin (omnimedia.app) with the bridge present,
 * which flips `isOfflineCapablePlayer()` true there too. Registering a service
 * worker on the production control origin would let it intercept the hub's own
 * navigations/API. So also require this browsing context to actually BE the
 * player surface (the loopback player window / a `?surface=player` tab); the
 * online-first control hub and the online-first web player never register one.
 */
export function registerServiceWorker(): void {
  if (!isOfflineCapablePlayer() || !isPlayerSurface()) {
    return;
  }

  if (typeof navigator === "undefined" || !("serviceWorker" in navigator)) {
    return;
  }

  const base = import.meta.env.BASE_URL || "/";
  const swUrl = `${base}sw.js`;

  window.addEventListener("load", () => {
    navigator.serviceWorker.register(swUrl, { scope: base }).catch(() => {
      // Offline support is an enhancement; ignore registration errors.
    });
  });
}
